import React, { useState, useRef, useCallback } from "react";
import { Button } from "@mui/material";
import Modal from "../../../shared/components/ui/Modal";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoIcon from "../../../assets/icons/CopyIcon.svg";

const BulkImportModal = ({ show, onCancel, onImport }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((file) => {
    if (file && file.type === "text/csv") {
      setSelectedFile(file);
    } else {
      alert("Please select a valid CSV file");
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e) => {
      const file = e.target.files[0];
      handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const handleDownloadTemplate = useCallback(() => {
    // Create CSV template
    const csvContent =
      "First Name,Last Name,Email Address,To Address,Delivery Address,Mobile Number,SMS Mode,Timezone\nJohn,Doe,john.doe@example.com,pbx@mail.com,john.doe@companymail.com,+17079811646,Enabled,PKT";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "user_import_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleImport = useCallback(() => {
    if (selectedFile) {
      onImport(selectedFile);
      setSelectedFile(null);
    }
  }, [selectedFile, onImport]);

  const handleCancelModal = useCallback(() => {
    setSelectedFile(null);
    onCancel();
  }, [onCancel]);

  return (
    <Modal
      className="shadow !rounded-2xl max-sm:!p-6"
      bodyClass="!p-0"
      show={show}
      header={
        <h2 className=" sm:text-lg md:text-xl font-bold text-content text-left">
          Bulk Import Users
        </h2>
      }
      description={
        <p className="text-sm sm:text-base text-content mt-1 text-left">
          Upload a CSV file to import multiple users at once
        </p>
      }
      footer={
        <div className="flex gap-3 justify-end">
          <Button
            onClick={handleImport}
            disabled={!selectedFile}
            variant="contained"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              outline: "1px solid #011A3C",
            }}
            className="disabled:!text-primary !text-xs sm:!text-sm"
          >
            IMPORT USER
          </Button>
          <Button
            onClick={handleCancelModal}
            variant="outlined"
            className="!text-xs sm:!text-sm"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              outline: "1px solid #011A3C",
            }}
          >
            CANCEL
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 px-0 p-4 pr-2">
        {/* Download Template Button */}
        <Button
          onClick={handleDownloadTemplate}
          startIcon={<FileDownloadOutlinedIcon />}
          variant="contained"
          fullWidth
          sx={{
            textTransform: "uppercase",
          }}
          className="bg-primary !text-sm !py-3 !rounded-md sm:!font-bold"
        >
          DOWNLOAD CSV TEMPLATE
        </Button>

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed border-content rounded-lg p-5 sm:p-8 pt-2 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isDragging
              ? "border-primary bg-primary/5"
              : "border-gray-300 bg-gray-50"
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            className="hidden"
          />
          <svg
            width="163"
            height="163"
            viewBox="0 0 163 163"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-[100px] sm:w-auto h-28 sm:h-auto"
          >
            <path
              d="M81.4946 36.7689L86.2963 31.9672L81.4946 27.1655L76.6929 31.9672L81.4946 36.7689ZM74.7029 97.8939C74.7029 99.6951 75.4185 101.423 76.6922 102.696C77.9658 103.97 79.6933 104.686 81.4946 104.686C83.2959 104.686 85.0233 103.97 86.297 102.696C87.5707 101.423 88.2863 99.6951 88.2863 97.8939H74.7029ZM120.255 65.9255L86.2963 31.9672L76.6929 41.5706L110.651 75.5289L120.255 65.9255ZM76.6929 31.9672L42.7346 65.9255L52.338 75.5289L86.2963 41.5706L76.6929 31.9672ZM74.7029 36.7689L74.7029 97.8939H88.2863L88.2863 36.7689H74.7029Z"
              fill="#011A3C"
            />
            <path
              d="M33.9585 108.666V115.458C33.9585 119.06 35.3896 122.515 37.937 125.063C40.4843 127.61 43.9393 129.041 47.5418 129.041H115.458C119.061 129.041 122.516 127.61 125.063 125.063C127.611 122.515 129.042 119.06 129.042 115.458V108.666"
              stroke="#011A3C"
              strokeWidth="4"
            />
          </svg>
          <p className="text-base font-bold text-content mb-1">
            Click to upload CSV file
          </p>
          <p className="text-sm text-content font-normal">or drag and drop</p>
          {selectedFile && (
            <p className="mt-3 text-sm font-medium text-green-600">
              Selected: {selectedFile.name}
            </p>
          )}
        </div>

        {/* Requirements Box */}
        <div className="bg-primary p-4 rounded-lg flex items-start gap-3 text-white">
          <img src={InfoIcon} alt="Info" className="mt-1" />
          <div className="text-[10px] sm:text-xs font-light">
            <strong className="text-sm font-semibold">
              CSV Format Requirements:
            </strong>
            <br />
            Your CSV should include columns: Full Name, Email, From Address, To
            Address, Delivery Address, Mobile Number, SMS Mode, Role, Timezone
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BulkImportModal;
